/**
 * Project Member API endpoints
 */
import { IremoveProjectMembers } from '@/types/project';
import axiosClient from './axiosClient';
import { ProjectMember, AddProjectMemberData } from '@/types';

// Simple in-memory cache to avoid repeated calls across views
type CacheKey = string;
const membersCache = new Map<CacheKey, { ts: number; data: ProjectMember[] }>();
const CACHE_TTL_MS = 60_000; // 1 minute

// In-flight request map: when N callers ask for the same key concurrently
// (e.g. 80 TaskCards mounting at once on the Tasks page), only the first one
// makes a network request — the rest await the same promise. Without this,
// the TTL cache is useless against a thundering herd because every caller
// misses cache before the first response lands.
const inFlightMembers = new Map<CacheKey, Promise<ProjectMember[]>>();
const inFlightMembership = new Map<string, Promise<{ is_member: boolean; member: any | null }>>();

export const projectMemberAPI = {
  /**
   * Get all members of a project
   */
  getProjectMembers: async (
    projectId: string,
    isActive: boolean = true,
    options?: { skip?: number; limit?: number, membership_type?: any[] }
  ): Promise<ProjectMember[]> => {
    const skip = options?.skip ?? 0;
    const limit = options?.limit ?? undefined;
    const membershipKey = options?.membership_type?.join(',') 
    const key: CacheKey = `${projectId}:${isActive ? '1' : '0'}:${skip}:${limit ?? 'all'}:${membershipKey}`;
    
    const now = Date.now();
    const cached = membersCache.get(key);
    if (cached && (now - cached.ts) < CACHE_TTL_MS) {
      return cached.data;
    }

    // Coalesce concurrent identical calls into a single in-flight request.
    const existing = inFlightMembers.get(key);
    if (existing) return existing;

    const params = new URLSearchParams();
    params.set('is_active', String(isActive));
    if (typeof skip === 'number') params.set('skip', String(skip));
    if (typeof limit === 'number') params.set('limit', String(limit));
    if (options?.membership_type?.length) {
      options.membership_type.forEach((type) => params.append('membership_type', type));
    }

    const promise = (async () => {
      try {
        const response = await axiosClient.get<ProjectMember[]>(
          `/project-members/projects/${projectId}/members?${params.toString()}`
        );
        const data = response.data || [];
        membersCache.set(key, { ts: Date.now(), data });
        return data;
      } finally {
        inFlightMembers.delete(key);
      }
    })();

    inFlightMembers.set(key, promise);
    return promise;
  },

  /**
   * Add a member to a project
   */
  addMember: async (projectId: string, memberData: AddProjectMemberData): Promise<ProjectMember> => {
    const response = await axiosClient.post<ProjectMember>(
      `/project-members/projects/${projectId}/members`,
      memberData
    );
    return response.data;
  },

  /**
   * Add multiple members to a project
   */
  bulkAddMembers: async (
    projectId: string,
    userIds: string[],
    role: 'admin' | 'member' | 'viewer' = 'member',
    membership_type:string,
  ): Promise<ProjectMember[]> => {
    const response = await axiosClient.post<ProjectMember[]>(
      `/project-members/projects/${projectId}/members/bulk`,
      { user_ids: userIds, role,membership_type }
    );
    return response.data;
  },

  /**
   * Update a project member's role
   */
  updateMember: async (memberId: string, role: 'admin' | 'member' | 'viewer'): Promise<ProjectMember> => {
    const response = await axiosClient.put<ProjectMember>(
      `/project-members/project-members/${memberId}`,
      { role }
    );
    return response.data;
  },

  /**
   * Remove a member from a project
   */
  removeMember: async (memberId: string): Promise<void> => {
    await axiosClient.delete(`/project-members/project-members/${memberId}`);
  },

  /**
   * Remove a user from a project by user ID
   */
  removeUserFromProject: async (projectId: string, userId: string): Promise<void> => {
    await axiosClient.delete(`/project-members/projects/${projectId}/members/${userId}`);
  },

  /**
   * Get member count for a project
   */
  getMemberCount: async (projectId: string): Promise<number> => {
    const response = await axiosClient.get<{ count: number }>(
      `/project-members/projects/${projectId}/members/count`
    );
    return response.data.count;
  },

  /**
   * Check if user is a member of the project
   */
  checkMembership: async (projectId: string, userId: string): Promise<{
     is_member: boolean; member: any | null;
}> => {
    // Coalesce concurrent identical lookups (no TTL cache here — membership
    // changes are driven by user actions, but bursts of duplicate calls
    // during a single render cycle should still collapse to one HTTP call).
    const key = `${projectId}:${userId}`;
    const existing = inFlightMembership.get(key);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const response = await axiosClient.get<{ is_member: boolean; member: any | null; }>(
          `/project-members/projects/${projectId}/members/check/${userId}`
        );
        return response.data;
      } finally {
        inFlightMembership.delete(key);
      }
    })();

    inFlightMembership.set(key, promise);
    return promise;
  },

  /**
   * Invalidate cache for a project (call after adding/removing/updating members)
   */
  invalidateCache: (projectId: string) => {
    // Remove all cache entries for this project
    const keysToDelete: string[] = [];
    membersCache.forEach((_, key) => {
      if (key.startsWith(`${projectId}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => membersCache.delete(key));
  },

  deleteMembersFromProject: async (projectId: string, memberData: IremoveProjectMembers): Promise<any> => {
    const response = await axiosClient.post<any>(
      `/project-members/projects/${projectId}/remove-project-members`,
      memberData
    );
    return response.data;
  },
};

